import { api, apiRequest } from '../api';

// Admin → Master chart template (COA-1). The client calls of server/src/routes/coaMaster.ts, which is
// mounted behind requireAuth + requireAdmin — so a non-admin login is refused by the SERVER, whatever
// this screen draws. Every call here is a GET or a download except ONE: addOverride, which appends a
// firm judgement to the layer BESIDE the customer's file (COA-5). The customer's file itself has no
// write call at all.
//
// ⛔ The shapes below are what the server returns, field for field. The screen computes no count,
// no classification and no translation of its own; a figure it needs and these do not carry is a
// server change, not something to work out in the browser.

export type CoaTemplateRef = {
  id: string; name: string; sourceFile: string; sourceSha256: string; rowCount: number;
  importedAt: number; importedBy: string;
};
export type CoaImportRecord = {
  id: number; templateName: string; sourceFile: string; sourceSha256: string;
  outcome: 'loaded' | 'refused' | string; reason: string; templateId: string; actor: string;
  times: number; firstAt: number; lastAt: number;
};
/** What an Account Type word IS — the server's answer (server/src/services/coaRules.ts kindOf), sent on
 *  every row, facet and observation. 'posting' and 'unknown' wear no badge. The screen has no rule of
 *  its own for it. */
export type CoaKind = 'header' | 'control' | 'contra' | 'system' | 'reference' | 'posting' | 'unknown';
export type CoaBrief = { code: string; name_th: string; name_en: string; account_type: string; kind: CoaKind };
export type CoaSummary = {
  id: string; name: string; sourceFile: string; sourceSha256: string; sourceSheet: string; contentSha256: string;
  rowCount: number; importedAt: number; importedBy: string;
  header: string[];
  readme: string[][];
  counts: {
    accounts: number; mainCategories: number; subgroups: number; accountTypes: number; topLevel: number;
    maxDepth: number; depthHistogram: Record<string, number>; normalBalance: Record<string, number>; withNotes: number;
  };
  facets: {
    mainCategory: { value: string; n: number }[];
    accountType: { value: string; n: number; codes: string[]; kind: CoaKind }[];
    subgroup: { value: string; n: number }[];
  };
  observations: {
    headers: { total: number; withChildren: number; withoutChildren: CoaBrief[] };
    nonHeaderParents: (CoaBrief & { children: number })[];
    balanceDiffersFromTop: (CoaBrief & { normal_balance: string; top_code: string; top_balance: string })[];
    categoryDiffersFromParent: (CoaBrief & { main_category: string; parent_code: string; parent_category: string })[];
    sameNameEn: { name: string; accounts: (CoaBrief & { parent_code: string; children: number })[] }[];
    sameNameTh: { name: string; accounts: (CoaBrief & { parent_code: string; children: number })[] }[];
  };
  /** The firm layer on this template (server/src/services/coaMasterEffective.ts). */
  firm: CoaFirmSummary;
};

// ── The firm layer (COA-5) ──────────────────────────────────────────────────────────────────────
export type CoaFirmField = 'account_type' | 'control_role' | 'name_th' | 'name_en' | 'subgroup' | 'system_notes';
/** One cell the firm rules on: the customer's value, the firm's, why, who, when — and whether it still stands. */
export type CoaFirmCell = {
  code: string; field: CoaFirmField; value: string; customerValue: string; firmValue: string; sourceValue: string;
  overridden: boolean; action: 'set' | 'confirm'; reason: string; decisionRef: string; author: string;
  recordedBy: string; recordedByName: string; at: number; overrideId: number; seeded: boolean; stale: boolean; staleWhy: string;
};
export type CoaKindCounts = {
  header: number; posting: number; control: number; contra: number; system: number; reference: number; unknown: number;
  postable: number; ar: string[]; ap: string[];
};
export type CoaFirmHistoryRow = {
  id: number; code: string; field: string; action: string; sourceValue: string; firmValue: string; reason: string;
  decisionRef: string; author: string; recordedBy: string; recordedByName: string; at: number; seeded: boolean;
};
export type CoaFirmSummary = {
  ok: boolean; reasons: string[]; intact: boolean; fields: CoaFirmField[]; words: string[]; unknownWords: string[];
  /** The control roles a judgement may set (the server's rule list: 'ar', 'ap'). */
  controlRoles: string[];
  maxOverrideId: number;
  counts: { customer: CoaKindCounts; effective: CoaKindCounts };
  cells: CoaFirmCell[];
  parents: { total: number; judged: number; codes: { code: string; judged: boolean }[] };
  history: CoaFirmHistoryRow[];
};
export type CoaOverrideBody = {
  code: string; field: CoaFirmField; action: 'set' | 'confirm' | 'withdraw';
  sourceValue: string; firmValue?: string; reason: string; decisionRef?: string;
};
export type CoaAccount = {
  code: string; name_th: string; name_en: string; main_category: string; subgroup: string; account_type: string;
  parent_code: string; normal_balance: string; system_notes: string; sort_order: number; depth: number;
  source_row: number; children: number; match: boolean;
  /** The server's kind of the type shown on this row — the customer's word, or in the firm view the
   *  word in force. */
  kind: CoaKind;
  /** view=firm only: the customer's own values (the row's fields above are then the layer's), and the
   *  firm's — the kind the server gives the type in force, the control role, every judged cell. */
  customer?: { account_type: string; name_th: string; name_en: string; subgroup: string; system_notes: string; kind: CoaKind };
  firm?: { kind: CoaKind; customerKind: CoaKind; controlRole: string; cells: Partial<Record<CoaFirmField, CoaFirmCell>> };
};
export type CoaView = 'customer' | 'firm';
export type CoaAccountQuery = { q?: string; category?: string; type?: string; view?: CoaView };
export type CoaAccountPage = { templateId: string; view?: CoaView; total: number; matched: number; page: number; limit: number; rows: CoaAccount[] };

const BASE = '/admin/coa-master';
const one = (id: string) => `${BASE}/${encodeURIComponent(id)}`;

/** The one place the screen's search and filters become a query string. `tree=1` always: the
 *  screen draws a tree, so the server adds every match's ancestors (match:false) to draw it down to. */
export const coaAccountQs = (f: CoaAccountQuery = {}): URLSearchParams => {
  const qs = new URLSearchParams();
  const q = String(f.q || '').trim();
  if (q) qs.set('q', q);
  if (f.category) qs.set('category', f.category);
  if (f.type) qs.set('type', f.type);
  if (f.view === 'firm') qs.set('view', 'firm');
  qs.set('tree', '1');
  return qs;
};

export const coaMasterApi = {
  list: (): Promise<{ templates: CoaTemplateRef[]; imports: CoaImportRecord[] }> => apiRequest('GET', BASE),
  get: (id: string): Promise<CoaSummary> => apiRequest('GET', one(id)),
  accounts: (id: string, f: CoaAccountQuery = {}): Promise<CoaAccountPage> =>
    apiRequest('GET', `${one(id)}/accounts?${coaAccountQs(f)}`),
  /** The whole template as the server writes it — every account, whatever the screen is filtering.
   *  Pass coaMasterExportName(sourceFile) as the name: the browser saves under the name given HERE, so
   *  this is the name a person actually ends up with, whatever the response header says. */
  exportXlsx: (id: string, filename: string): Promise<void> =>
    api.awExport(`${one(id)}/export.xlsx`, new URLSearchParams(), filename),
  /** ⛔ The tab's one write: append a firm judgement (set / confirm / withdraw) to the layer beside the
   *  customer's file. Refused with 400 and the server's reasons (err.payload.problems) when a rule says no. */
  addOverride: (id: string, body: CoaOverrideBody): Promise<{ id: number; before: string; after: string; firm: CoaFirmSummary }> =>
    apiRequest('POST', `${one(id)}/overrides`, body),
};

/** The name a downloaded copy of a template is SAVED under.
 *  ⛔ Never the customer's own file name: the export is a workbook rebuilt from the stored rows — equal cell
 *  for cell, different bytes and SHA-256, and without the customer's Excel table. Saved under the original
 *  name it would sit in a Downloads folder posing as the file the customer sent. routes/coaMaster.ts gives
 *  the response the same name; the two must not drift (testCoaMasterScreen asserts the saved one). */
export const coaMasterExportName = (sourceFile: string): string =>
  (String(sourceFile || '').trim() || 'master-coa.xlsx').replace(/(\.xlsx)?$/i, ' (export).xlsx');
